import { useState, useEffect } from 'react';
import { XMarkIcon, Cog8ToothIcon } from '@heroicons/react/24/solid';
import GlassyIcon from '../common/GlassyIcon';
import ToggleSwitch from '../common/ToggleSwitch';
import ActionButton from '../common/ActionButton';

const SettingRow = ({ title, description, children, disabled = false }) => (
  <div
    className={`
      relative flex items-center justify-between py-3 px-4 rounded-2xl
      backdrop-blur-sm border border-border overflow-hidden
      transition-all duration-200 hover:shadow-md
      ${disabled ? 'opacity-50 pointer-events-none' : ''}
    `}
    style={{
      background: 'linear-gradient(135deg, var(--color-background-primary), var(--color-background-primary))',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    }}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-30 dark:from-white/5 dark:opacity-20" />
    <div className="relative z-10 flex items-center justify-between w-full">
      <div className="pr-4 flex-grow">
        <h4 className="font-semibold text-content-primary mb-1">{title}</h4>
        <p className="text-sm text-content-secondary leading-relaxed">{description}</p>
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  </div>
);

const SectionHeader = ({ label }) => (
  <div className="flex items-center gap-3 pt-2 pb-1">
    <span className="text-xs font-bold uppercase tracking-widest text-content-secondary opacity-60">{label}</span>
    <div className="flex-1 h-px bg-border opacity-60" />
  </div>
);

const INPUT_MODES = [
  { id: 'kana', label: 'Kana', sub: 'ひ only' },
  { id: 'mixed', label: 'Mixed', sub: 'ひ or 漢' },
  { id: 'kanji', label: 'Strict Kanji', sub: '漢 only' },
];

const InputModeSelector = ({ value, onChange, disabled }) => (
  <div className={`flex gap-2 w-full mt-3 transition-opacity duration-200 ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
    {INPUT_MODES.map((mode) => {
      const isActive = value === mode.id;
      return (
        <button
          key={mode.id}
          type="button"
          onClick={() => onChange(mode.id)}
          className={`relative flex-1 flex flex-col items-center py-2 px-1 rounded-xl border overflow-hidden transition-all duration-200 ${
            isActive
              ? 'border-secondary shadow-md shadow-purple-500/40'
              : 'border-border hover:shadow-md'
          }`}
          style={{ background: isActive ? 'var(--color-secondary)' : 'var(--color-background-primary)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-70 dark:from-white/10 dark:opacity-50" />
          <span className={`relative z-10 text-xs sm:text-sm font-semibold ${isActive ? 'text-white' : 'text-content-primary'}`}>{mode.label}</span>
          <span className={`relative z-10 text-xs mt-0.5 ${isActive ? 'text-white/80' : 'text-content-secondary'}`}>{mode.sub}</span>
        </button>
      );
    })}
  </div>
);

const SessionSettingsModal = ({ onClose, updateSettings, style, onMouseDown }) => {
  const [settings, setSettings] = useState(() => {
    const savedSettings = localStorage.getItem('sessionSettings');
    return savedSettings
      ? JSON.parse(savedSettings)
      : {
          playCorrectSound: true,
          playWrongSound: true,
          showTranslation: true,
          inputMode: 'mixed',
          simpleAnswerMode: false,
          showFlippingAnimation: true,
          audioHotkeyUseBacktick: false,
          reducedAnimations: false,
          showKaraokeMode: true,
          autoPlayAfterAnswer: false,
          autoConvertToKana: true,
          stayOnQuestionUntilCorrect: false,
          keepAudioDeviceAwake: false,
        };
  });

  useEffect(() => {
    updateSettings(settings);
    localStorage.setItem('sessionSettings', JSON.stringify(settings));
  }, [settings, updateSettings]);

  const handleSettingChange = (key, value) => {
    setSettings((prevSettings) => ({ ...prevSettings, [key]: value }));
  };

  return (
    <div
      className="bg-background-secondary rounded-2xl shadow-2xl w-full max-w-lg border border-border border-t-4 flex flex-col max-h-[90vh] sm:max-h-[80vh]"
      style={{
        borderTopColor: 'var(--color-secondary)',
        ...style,
      }}
      onMouseDown={onMouseDown}
    >
      {/* Header */}
      <div className="relative p-4 sm:p-5 flex justify-between items-center overflow-hidden flex-shrink-0">
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-70 dark:from-white/5 dark:opacity-40" />
        <div className="relative z-10 flex items-center">
          <GlassyIcon icon={Cog8ToothIcon} size="sm" color="purple" className="mr-2 sm:mr-3" />
          <h3 className="text-base sm:text-lg font-bold text-content-primary">Session Settings</h3>
        </div>
        <button onClick={onClose} className="relative z-10 p-1 rounded-full text-content-secondary hover:bg-background-primary transition-colors">
          <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
        </button>
      </div>

      {/* Settings content - scrollable */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-2 sm:py-4">
        <div className="space-y-2">

          <SectionHeader label="Audio" />
          <SettingRow title="Play Correct Sound" description="Audio feedback for correct answers.">
            <ToggleSwitch enabled={settings.playCorrectSound} setEnabled={(val) => handleSettingChange('playCorrectSound', val)} color="secondary" />
          </SettingRow>
          <SettingRow title="Play Wrong Sound" description="Audio feedback for incorrect answers.">
            <ToggleSwitch enabled={settings.playWrongSound} setEnabled={(val) => handleSettingChange('playWrongSound', val)} color="secondary" />
          </SettingRow>
          <SettingRow title="Auto-play After Answer" description="Replay the sentence audio automatically after each answer.">
            <ToggleSwitch enabled={settings.autoPlayAfterAnswer} setEnabled={(val) => handleSettingChange('autoPlayAfterAnswer', val)} color="secondary" />
          </SettingRow>
          <SettingRow
            title="Audio Hotkey: Backtick (`)"
            description="Use backtick (`) instead of Space to play audio. Helpful when typing in Japanese."
          >
            <ToggleSwitch enabled={settings.audioHotkeyUseBacktick} setEnabled={(val) => handleSettingChange('audioHotkeyUseBacktick', val)} color="secondary" />
          </SettingRow>
          <SettingRow
            title="Keep Bluetooth Awake"
            description="Plays an inaudible tone so Bluetooth headphones don't sleep and cut off the start of audio."
          >
            <ToggleSwitch enabled={settings.keepAudioDeviceAwake ?? false} setEnabled={(val) => handleSettingChange('keepAudioDeviceAwake', val)} color="secondary" />
          </SettingRow>

          <SectionHeader label="Input" />
          <SettingRow title="Simple Answer Mode" description="Use buttons instead of typing the answer.">
            <ToggleSwitch enabled={settings.simpleAnswerMode} setEnabled={(val) => handleSettingChange('simpleAnswerMode', val)} color="secondary" />
          </SettingRow>
          <div
            className={`relative flex flex-col py-3 px-4 rounded-2xl backdrop-blur-sm border border-border overflow-hidden transition-all duration-200 hover:shadow-md ${settings.simpleAnswerMode ? 'opacity-50' : ''}`}
            style={{
              background: 'linear-gradient(135deg, var(--color-background-primary), var(--color-background-primary))',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-30 dark:from-white/5 dark:opacity-20" />
            <div className="relative z-10">
              <h4 className={`font-semibold mb-1 ${settings.simpleAnswerMode ? 'text-content-secondary' : 'text-content-primary'}`}>Input Mode</h4>
              <p className="text-sm text-content-secondary leading-relaxed">
                {settings.simpleAnswerMode
                  ? 'Not available in Simple Answer Mode.'
                  : settings.inputMode === 'mixed'
                  ? 'Mixed: accept any correct form — kana or kanji per word. Great for practising what you know.'
                  : settings.inputMode === 'kanji'
                  ? 'Strict Kanji: requires the exact kanji form. Kana-only words (particles etc.) always pass.'
                  : 'Kana: answer entirely in hiragana/katakana — kanji will not be accepted.'}
              </p>
              <InputModeSelector value={settings.inputMode || 'mixed'} onChange={(val) => handleSettingChange('inputMode', val)} disabled={settings.simpleAnswerMode} />
            </div>
          </div>
          <SettingRow
            title="Auto-convert to Kana"
            description="Automatically convert romaji to kana as you type. Disable if using handwriting or IME input to avoid conflicts with character recognition."
            disabled={settings.simpleAnswerMode}
          >
            <ToggleSwitch enabled={settings.autoConvertToKana ?? true} setEnabled={(val) => handleSettingChange('autoConvertToKana', val)} color="secondary" />
          </SettingRow>
          <SettingRow
            title="Stay Until Correct"
            description="After a wrong answer, keep you on the same question so you can fix typos and try again instead of moving to a new audio clip."
          >
            <ToggleSwitch enabled={settings.stayOnQuestionUntilCorrect ?? false} setEnabled={(val) => handleSettingChange('stayOnQuestionUntilCorrect', val)} color="secondary" />
          </SettingRow>

          <SectionHeader label="Visual" />
          <SettingRow title="Show Translation" description="Automatically reveal the English translation.">
            <ToggleSwitch enabled={settings.showTranslation} setEnabled={(val) => handleSettingChange('showTranslation', val)} color="secondary" />
          </SettingRow>
          <SettingRow title="Card Flip Animation" description="Animate the card when revealing the answer.">
            <ToggleSwitch enabled={settings.showFlippingAnimation} setEnabled={(val) => handleSettingChange('showFlippingAnimation', val)} color="secondary" />
          </SettingRow>
          <SettingRow title="Karaoke Highlighting" description="Highlight words in the sentence as they are spoken during audio playback.">
            <ToggleSwitch enabled={settings.showKaraokeMode} setEnabled={(val) => handleSettingChange('showKaraokeMode', val)} color="secondary" />
          </SettingRow>
          <SettingRow title="Reduced Animations" description="Disable bouncing badge and icon animations for a calmer experience.">
            <ToggleSwitch enabled={settings.reducedAnimations} setEnabled={(val) => handleSettingChange('reducedAnimations', val)} color="secondary" />
          </SettingRow>

        </div>
      </div>

      {/* Footer */}
      <div
        className="px-4 sm:px-6 py-3 sm:py-4 flex justify-end border-t border-border flex-shrink-0"
        style={{ background: 'var(--color-background-primary)' }}
      >
        <ActionButton variant="glassy" type="button" onClick={onClose} color="purple" className="w-full px-4 sm:px-6 py-2 sm:py-3 text-white">
          Close
        </ActionButton>
      </div>
    </div>
  );
};

export default SessionSettingsModal;
